/**
 * Net Exposure books — Lab `net-exposure.js` dual-call pattern via CM-API.
 *
 * On load / filter change:
 *   1. `POST /v1/book/getBooksForBackendPre` — fast inventory (NE cells empty)
 *   2. `POST /v1/book/getBooksForBackend` — horse P/L + netExposure
 * Poll (~3s) refreshes only the full endpoint.
 *
 * CM callers always use level "Company Master" (hierarchyLevel === 1).
 */

import { useQuery } from '@tanstack/react-query'
import { cmApi, type BooksForBackendRequest } from '@/api/cmApi'
import {
  backendGroupsToEvents,
  preGroupsToEvents,
  toBetfairNetExposureEvents,
} from '@/api/map/books'
import { useBookmakerSessionFlag } from '@/api/hooks/useCmMe'
import { authHeaders } from '@/auth/session'
import { ROLE_LABEL } from '@/features/hierarchy/hierarchy'
import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import {
  type BookType,
  type NetExposureEvent,
  type SportName,
} from '@/features/net-exposure/fixture'

/** Lab polls Net Exposure every 3s (`reloadTime`). */
export const BOOKS_POLL_MS = 3_000

/** `betfair` = Lab Betfair Net Exposure (`netExposureNew`) — exchange Match Odds only. */
export type BooksScope = 'all' | 'betfair'

export type BooksQuery = {
  selectedType: BookType
  eventType: SportName
  eventname?: string
  /** Lab Net Exposure vs Betfair Net Exposure page. Default `all`. */
  scope?: BooksScope
}

const CM_LEVEL = ROLE_LABEL['company-master']

/** Books body for CM-API — Betfair NE sets `betfairNetExposure` (is_placed_on_vendor filter). */
export function buildRequest(
  query: BooksQuery,
  user: string,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
): BooksForBackendRequest {
  return {
    selectedType: query.selectedType,
    ...(query.scope === 'betfair' ? { betfairNetExposure: true } : {}),
    filter: {
      user,
      level: CM_LEVEL,
      eventType: query.eventType === 'All' ? 'All' : query.eventType,
      eventname: query.eventname?.trim() || 'All',
      bookmakerSessionFlag,
    },
  }
}

function queryKeyFields(
  query: BooksQuery,
  user: string | undefined,
  bookmakerSessionFlag: BookmakerSessionFlag,
) {
  return {
    ...query,
    scope: query.scope ?? 'all',
    user,
    level: CM_LEVEL,
    bookmakerSessionFlag,
  }
}

function applyScope(events: NetExposureEvent[], scope: BooksScope | undefined): NetExposureEvent[] {
  return scope === 'betfair' ? toBetfairNetExposureEvents(events) : events
}

/**
 * Prefer full books. Betfair NE never paints Pre inventory (empty horse / Bf-Book shells).
 */
export function resolveBooksEvents(
  scope: BooksScope | undefined,
  full: NetExposureEvent[] | undefined,
  pre: NetExposureEvent[] | undefined,
): NetExposureEvent[] {
  if (scope === 'betfair') return full ?? []
  return full ?? pre ?? []
}

async function loadBooksPre(
  query: BooksQuery,
  user: string,
  bookmakerSessionFlag: BookmakerSessionFlag,
): Promise<NetExposureEvent[]> {
  const groups = await cmApi.getBooksForBackendPre(
    authHeaders(),
    buildRequest(query, user, bookmakerSessionFlag),
  )
  // Lab Betfair NE never fans out Artemis PB groups.
  const src =
    query.scope === 'betfair' ? (groups ?? []).filter((g) => g.key !== 'PB') : (groups ?? [])
  return applyScope(preGroupsToEvents(src, query.eventType), query.scope)
}

async function loadBooks(
  query: BooksQuery,
  user: string,
  bookmakerSessionFlag: BookmakerSessionFlag,
): Promise<NetExposureEvent[]> {
  const groups = await cmApi.getBooksForBackend(
    authHeaders(),
    buildRequest(query, user, bookmakerSessionFlag),
  )
  const src =
    query.scope === 'betfair' ? (groups ?? []).filter((g) => g.key !== 'PB') : (groups ?? [])
  return applyScope(backendGroupsToEvents(src, query.eventType), query.scope)
}

/**
 * Resolve CM agent id for books/ticker filter.user.
 * Prefer hierarchy self; fall back to JWT `/v1/me` agentId when hierarchy is down.
 */
export async function resolveCmBookUserId(): Promise<string> {
  try {
    const self = await cmApi.hierarchySelf(authHeaders())
    if (self?.node?.id) return self.node.id
  } catch {
    // Local stub / hierarchy down — JWT agentId is enough for ticker scope.
  }
  const me = await cmApi.me(authHeaders())
  const id = me.caller.agentId?.trim()
  if (!id) throw new Error('CM agentId missing from /v1/me')
  return id
}

export function useCmBookUser() {
  return useQuery({
    queryKey: ['cm', 'book-user'],
    queryFn: resolveCmBookUserId,
    staleTime: 60_000,
  })
}

/** Lab `getBooksForBackendPre` — inventory only (NE cells 0). Skipped for Betfair NE. */
export function useBooksForBackendPre(query: BooksQuery) {
  const userQ = useCmBookUser()
  const user = userQ.data
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  const betfair = query.scope === 'betfair'

  return useQuery({
    queryKey: ['cm', 'books-pre', queryKeyFields(query, user, bookmakerSessionFlag)],
    queryFn: () => loadBooksPre(query, user!, bookmakerSessionFlag),
    enabled: Boolean(user) && !betfair,
    staleTime: BOOKS_POLL_MS,
    placeholderData: betfair ? undefined : (previous) => previous,
  })
}

/** Lab `getBooksForBackend` — horse P/L + NE; polls while live. */
export function useBooksForBackend(query: BooksQuery) {
  const userQ = useCmBookUser()
  const user = userQ.data
  const bookmakerSessionFlag = useBookmakerSessionFlag()

  return useQuery({
    queryKey: ['cm', 'books', queryKeyFields(query, user, bookmakerSessionFlag)],
    queryFn: () => loadBooks(query, user!, bookmakerSessionFlag),
    enabled: Boolean(user),
    refetchInterval: BOOKS_POLL_MS,
    placeholderData: (previous) => previous,
  })
}

/**
 * Lab load path: fire Pre + full together; show Pre until full arrives, then
 * prefer full. Betfair NE waits on full only (no empty Pre shells).
 */
export function useNetExposureBooks(query: BooksQuery) {
  const userQ = useCmBookUser()
  const pre = useBooksForBackendPre(query)
  const full = useBooksForBackend(query)
  const betfair = query.scope === 'betfair'

  const events = resolveBooksEvents(query.scope, full.data, pre.data)
  const isLoading =
    userQ.isLoading ||
    (betfair
      ? full.isPending
      : !full.data && !pre.data && (full.isLoading || pre.isLoading))
  const isError = Boolean(
    userQ.isError || (betfair ? full.isError : full.isError && pre.isError),
  )
  const error = userQ.error ?? full.error ?? pre.error
  const isFetching = userQ.isFetching || full.isFetching || pre.isFetching

  return {
    events,
    isLoading,
    isError,
    error,
    isFetching,
    pre,
    full,
    refetch: () => {
      void userQ.refetch()
      void pre.refetch()
      void full.refetch()
    },
  }
}
